import { useState, useEffect } from 'react';
import { otpService, IOTPCodeParams, IOTPOptionsPayload } from '../services/otp-service';
import { getCustomerInfo, getTmxId } from '../utils/general';
import {
  validateOTPRequiredParams,
  formatVerificationCodeInput,
  getPriorityOTPOption,
  logOTPError,
  formatDisplayPhoneNumber
} from '../utils/otp-utils';
import {
  OTP_OPTIONS, OTP_ERROR_MESSAGES, OTP_ERROR_SOURCES, OTP_ERROR_TYPES, type OTPErrorSource
} from '../utils/consts';

export const useOTP = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [otpOptions, setOTPOptions] = useState<any>(null);
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [authID, setAuthID] = useState<string>('');
  const [validationError, setValidationError] = useState<string>('');
  const [isOTPDisabled, setIsOTPDisabled] = useState<boolean>(false);
  const [lastUsedOtpChannel, setLastUsedOtpChannel] = useState<string>('');
  const [otpErrorMessage, setOtpErrorMessage] = useState<string>('');
  const [otpErrorSource, setOtpErrorSource] = useState<OTPErrorSource | null>(null);
  // Get user information for API calls
  const { jwt, userId, userEmail } = getCustomerInfo();

  const tmxProfileId = getTmxId();

  const handleVerificationCodeChange = (value: string) => {
    const formattedValue = formatVerificationCodeInput(value, 6);
    setVerificationCode(formattedValue);
    // Only clear validation error when user starts typing if OTP is not disabled
    if (validationError && !isOTPDisabled) {
      setValidationError('');
    }
  };

  const clearVerificationCode = () => {
    setVerificationCode('');
  };

  const setOTPDisabled = (disabled: boolean) => {
    setIsOTPDisabled(disabled);
  };

  const clearOtpError = () => {
    setOtpErrorMessage('');
    setOtpErrorSource(null);
  };

  const getOTPOptions = async () => {
    const validation = validateOTPRequiredParams({ userEmail, jwt });
    if (!validation.isValid) {
      logOTPError('getOTPOptions', `Cannot fetch otp options - ${validation.error}`);
      setOtpErrorMessage(OTP_ERROR_MESSAGES.MISSING_DATA);
      setOtpErrorSource(OTP_ERROR_SOURCES.OPTIONS);
      return;
    }

    const payload: IOTPOptionsPayload = { email: userEmail! };
    const result = await otpService.getOTPOptions(payload, jwt!);

    if (result.success) {
      setOTPOptions(result.data);
    } else {
      // Map service error type to appropriate error message
      const errorType = result.errorType || OTP_ERROR_TYPES.GENERAL_FAILURE;
      const errorMessage = OTP_ERROR_MESSAGES[errorType];

      setOtpErrorMessage(errorMessage);
      setOtpErrorSource(OTP_ERROR_SOURCES.OPTIONS);
    }
  };

  const getOTPCode = async (otpChannel: string) => {
    const validation = validateOTPRequiredParams({ userEmail, userId, jwt });
    if (!validation.isValid) {
      logOTPError('getOTPCode', `Cannot send otp code - ${validation.error}`);
      setOtpErrorMessage(OTP_ERROR_MESSAGES.MISSING_DATA);
      setOtpErrorSource(OTP_ERROR_SOURCES.CODE);
      return;
    }

    const params: IOTPCodeParams = {
      email: userEmail!,
      userID: userId!,
      otpChannel,
      sessionID: tmxProfileId || ''
    };

    const result = await otpService.getOTPCode(params, jwt!);

    if (result.success && result.data?.encryptedPayload) {
      setAuthID(result.data.encryptedPayload);
      setLastUsedOtpChannel(otpChannel); // Track the channel used
      return;
    }

    // Retry with email if the original channel was 'sms' or 'phone' and failed
    if (otpChannel === OTP_OPTIONS.SMS || otpChannel === OTP_OPTIONS.PHONE) {
      logOTPError(
        'getOTPCode',
        `${otpChannel} failed, retrying with email`,
        { originalChannel: otpChannel, error: result.error }
      );

      const emailParams: IOTPCodeParams = {
        ...params,
        otpChannel: OTP_OPTIONS.EMAIL
      };

      const emailResult = await otpService.getOTPCode(emailParams, jwt!);

      if (emailResult.success && emailResult.data?.encryptedPayload) {
        setAuthID(emailResult.data.encryptedPayload);
        setLastUsedOtpChannel(OTP_OPTIONS.EMAIL); // Track that email was actually used
      } else {
        logOTPError(
          'getOTPCode',
          'Email retry also failed',
          { originalChannel: otpChannel, emailError: emailResult.error }
        );
        setLastUsedOtpChannel(OTP_OPTIONS.EMAIL); // Track that email was actually used
        setOtpErrorMessage(OTP_ERROR_MESSAGES.GENERAL_FAILURE);
        setOtpErrorSource(OTP_ERROR_SOURCES.CODE);
      }
    } else if (otpChannel === OTP_OPTIONS.EMAIL) {
      // Email was the original choice and it failed
      logOTPError('getOTPCode', 'Email failed as original channel', { error: result.error });
      setOtpErrorMessage(OTP_ERROR_MESSAGES.GENERAL_FAILURE);
      setOtpErrorSource(OTP_ERROR_SOURCES.CODE);
    } else {
      logOTPError('getOTPCode', `Failed to get OTP code for channel: ${otpChannel}`, { error: result.error });
    }
  };

  const handleCloseDrawer = () => {
    setVerificationCode(''); // Clear the input when closing
    setValidationError(''); // Clear any validation error when closing
    setIsOTPDisabled(false); // Reset OTP disabled state when closing
    setOtpErrorMessage(''); // Clear OTP failure message when closing
    setOtpErrorSource(null); // Clear error source when closing
  };

  const startOTPFlow = async () => {
    setValidationError(''); // Clear any previous errors
    setOtpErrorMessage(''); // Clear any OTP failure message
    setOtpErrorSource(null); // Clear error source
    await getOTPOptions();
  };

  const requestNewCodeWithChannel = async (channel: string, isRetryFlow: boolean = false) => {
    setValidationError(''); // Clear any previous errors
    setIsOTPDisabled(false); // Reset disabled state when starting fresh
    // Don't clear retry error message if this is a retry flow - it should persist until user attempts to verify
    if (!isRetryFlow) {
      setOtpErrorMessage('');
      setOtpErrorSource(null);
    }
    // await getOTPCode(OTP_OPTIONS.EMAIL);
    await getOTPCode(channel);
  };

  // Auto-trigger OTP code request when options are received
  useEffect(() => {
    if (otpOptions) {
      (async () => {
        const priorityOption = getPriorityOTPOption(otpOptions.otpOptions);
        if (priorityOption) {
          await requestNewCodeWithChannel(priorityOption);
        }
      })();
    }
  }, [otpOptions]);

  return {
    // State
    otpOptions,
    verificationCode,
    authID,
    validationError,
    isOTPDisabled,
    lastUsedOtpChannel,
    userEmail,
    otpErrorMessage,
    otpErrorSource,

    // Actions
    handleVerificationCodeChange,
    handleCloseDrawer,
    startOTPFlow,
    requestNewCodeWithChannel,
    clearVerificationCode,
    setValidationError,
    setOTPDisabled,
    setOtpErrorMessage,
    setOtpErrorSource,
    clearOtpError,

    // Computed values
    hasPhoneFromResponse: otpOptions?.phone,
    displayPhoneNumber: formatDisplayPhoneNumber(otpOptions?.phone)
  };
};
